/*
 * Platform-admin SMTP settings — host/port/auth/from/TLS for the
 * notification dispatcher's email channel. Replaces the env-only
 * configuration with a UI + encrypted-at-rest record. The dispatcher
 * picks up changes on its next drain (no worker restart).
 *
 * Source banner shows where the active config is coming from:
 *   database         — UI-stored record is in effect
 *   env-var-fallback — TALLYSTREAM_SMTP_* env vars are in effect; saving
 *                      the form here will take over and the env vars
 *                      become a dormant fallback
 *   none             — neither source is configured; email channel disabled
 */

import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import StatusBadge from "../components/StatusBadge.js";
import { api, ApiError } from "../lib/api.js";

export default function SmtpSettings() {
  const [host, setHost] = useState("");
  const [port, setPort] = useState<number>(587);
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [passwordWasSet, setPasswordWasSet] = useState(false);
  const [from, setFrom] = useState("alerts@tallystream.local");
  const [secure, setSecure] = useState(false);
  const [source, setSource] = useState<"database" | "env-var-fallback" | "none">("none");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const refresh = () => {
    api.getSmtpSettings()
      .then((s) => {
        setHost(s.host);
        setPort(s.port);
        setUser(s.user);
        setFrom(s.from);
        setSecure(s.secure);
        setSource(s.source);
        setUpdatedAt(s.updatedAt);
        setPasswordWasSet(s.passwordSet);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  };
  useEffect(refresh, []);

  async function save() {
    if (!host.trim()) { setError("Host is required."); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      // Empty password = "keep existing". Only ship the field when the
      // operator typed something.
      await api.setSmtpSettings({
        host: host.trim(), port, user, from,
        secure,
        ...(password ? { password } : {})
      });
      setNotice("Saved. Dispatcher picks this up on its next tick.");
      setPassword("");
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function sendTest() {
    if (!testTo.includes("@")) { setError("Enter a valid recipient address for the test."); return; }
    setTestBusy(true); setTestResult(null); setError(null);
    try {
      const r = await api.testSmtpSettings(testTo);
      setTestResult({ ok: r.ok, message: r.ok ? `Sent to ${r.sentTo}` : (r.error ?? "Send failed.") });
    } catch (e) {
      setTestResult({ ok: false, message: e instanceof ApiError ? e.message : String(e) });
    } finally { setTestBusy(false); }
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>SMTP settings</h1>
          <p className="muted">
            Outbound email transport for the notification dispatcher. Changes take effect within one
            dispatcher tick (default 30s) — no service restart needed.
          </p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {notice && <div className="ts-alert ts-alert-info">{notice}</div>}

      <section className="empty-state-card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Active source</h2>
        <p className="muted" style={{ fontSize: 13 }}>
          {source === "database" && (<>UI-stored record is in effect. Updated {updatedAt ?? "—"}. <StatusBadge status="active" /></>)}
          {source === "env-var-fallback" && (<>Falling back to <code>TALLYSTREAM_SMTP_*</code> env vars. Saving below will take over. <StatusBadge status="warning" tone="warn">env fallback</StatusBadge></>)}
          {source === "none" && (<>No SMTP configured. Email notifications are disabled. <StatusBadge status="not_configured" /></>)}
        </p>
      </section>

      <section className="empty-state-card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Configure</h2>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "2fr 1fr" }}>
          <label className="transfer-filter">
            Host
            <input type="text" value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.example.com" />
          </label>
          <label className="transfer-filter">
            Port
            <input type="number" value={port} onChange={(e) => setPort(Number(e.target.value))} placeholder="587" />
          </label>
          <label className="transfer-filter">
            Username
            <input type="text" value={user} onChange={(e) => setUser(e.target.value)} placeholder="alerts@example.com" />
          </label>
          <label className="transfer-filter">
            Password {passwordWasSet && !password ? <small style={{ color: "var(--text-muted)" }}>(leave blank to keep existing)</small> : null}
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={passwordWasSet ? "•••••••• (existing)" : "your SMTP password"} autoComplete="new-password" />
          </label>
          <label className="transfer-filter">
            From address
            <input type="email" value={from} onChange={(e) => setFrom(e.target.value)} placeholder="alerts@example.com" />
          </label>
          <label className="transfer-filter">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={secure} onChange={(e) => setSecure(e.target.checked)} />
              Implicit TLS (port 465)
            </span>
          </label>
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save SMTP settings"}
          </button>
          <button type="button" className="btn btn-secondary" onClick={refresh} disabled={busy}>Refresh</button>
        </div>
      </section>

      <section className="empty-state-card">
        <h2 style={{ marginTop: 0 }}>Test connection</h2>
        <p className="muted" style={{ fontSize: 13 }}>
          Sends a one-line test message using whichever source is currently active (DB record or env fallback).
          The recipient should receive it within seconds — if not, check the response below for the SMTP error.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label className="transfer-filter transfer-filter-grow">
            Recipient
            <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" />
          </label>
          <button type="button" className="btn btn-secondary" disabled={testBusy || !testTo} onClick={() => void sendTest()}>
            {testBusy ? "Sending…" : "Send test email"}
          </button>
        </div>
        {testResult && (
          <div className={testResult.ok ? "ts-alert ts-alert-info" : "ts-alert ts-alert-error"} style={{ marginTop: 12 }}>
            {testResult.message}
          </div>
        )}
      </section>
    </div>
  );
}
